import React from 'react';
import Sheet from '@mui/joy/Sheet';
import Table from '@mui/joy/Table';
import Stack from "@mui/joy/Stack";
import Typography from '@mui/joy/Typography';
import Chip from '@mui/joy/Chip';
import Link from '@mui/joy/Link';
import Tooltip from '@mui/joy/Tooltip';
import Box from '@mui/material/Box';
import List from "@mui/joy/List";
import ListItem from "@mui/joy/ListItem";
import ListItemContent from "@mui/joy/ListItemContent";
import ListDivider from "@mui/joy/ListDivider";
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import MarkEmailReadIcon from '@mui/icons-material/MarkEmailRead';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import SendIcon from '@mui/icons-material/Send';
import HourglassDisabledIcon from '@mui/icons-material/HourglassDisabled';
import TimerIcon from '@mui/icons-material/Timer';
import DoneIcon from '@mui/icons-material/Done';
import BuildIcon from '@mui/icons-material/Build';
import BrokenImageIcon from '@mui/icons-material/BrokenImage';
import CancelIcon from '@mui/icons-material/Cancel';
import FastRewindIcon from '@mui/icons-material/FastRewind';
import QuestionMarkIcon from '@mui/icons-material/QuestionMark';
import AutoStoriesIcon from '@mui/icons-material/AutoStories';
import IconButton from "@mui/joy/IconButton";
import {useParams} from "react-router-dom";
import {NotificationService} from "../../../common/notification";
import CircularProgress from "@mui/joy/CircularProgress";
import { offersListApi } from "../orderApi";
import OfferHistory from "../OfferHistory";
import { OfferInfo, OfferStatus, OfferTypeDescription, OfferStatusDescription, OfferCancellationReasonDescription, SteamApiTradeOfferState, SteamApiTradeStatus, OfferHandleMode, OfferHandleModeDescription } from '../../../models/order';
import {formatDate, formatDateTime} from "../../../common/dateTime";

export function Offers() {
    const {id} = useParams();
    const [offers, setOffers] = React.useState<OfferInfo[]>([]);
    const [loading, setLoading] = React.useState(false);
    const [selectedOffer, setSelectedOffer] = React.useState<number>(0);
    const [historyOpen, setHistoryOpen] = React.useState(false);

    React.useEffect(() => {
        if (id && Number(id) > 0) {
            loadData(Number(id)).catch(console.error);
        }
    }, [id])

    const loadData = async (id: number) => {
        setLoading(true);
        try {
            let result = await offersListApi(id);
            if (result.result) {
                const res = result.value!;
                setOffers(res)
            } else {
                NotificationService.alertError("Пожалуйста попробуйте позже или обратитесь в службу поддержки")
            }
        } finally {
            setLoading(false);
        }
    }

    const getStatusIcon = (status: OfferStatus): any => {
        switch(status) { 
            case OfferStatus.New: { 
               return <MailOutlineIcon/>;
            }
            case OfferStatus.CanBeSend: {
                return <MarkEmailReadIcon/>;
            }
            case OfferStatus.WaitingForConfirmation: {
                return <HourglassEmptyIcon/>;
            }
            case OfferStatus.WaitingForCancellation: {
                return <HourglassDisabledIcon/>;
            }
            case OfferStatus.Sent: { 
                return <SendIcon/>;
            }
            case OfferStatus.Accepted: { 
                return <DoneIcon/>;
            }
            case OfferStatus.Hold: { 
                return <TimerIcon/>;
            }
            case OfferStatus.Canceled: { 
                return <CancelIcon/>;
            }
            case OfferStatus.InvalidItems: { 
                return <BrokenImageIcon/>;
            }
            case OfferStatus.RolledBack: { 
                return <FastRewindIcon/>;
            }
            case OfferStatus.Undefined:
            default: { 
                return <QuestionMarkIcon/>;
             }
         }
    }

    const getStatusColor = (status: OfferStatus): 'primary' | 'neutral' | 'danger' | 'success' | 'warning' => {
        switch(status) { 
            case OfferStatus.New: { 
               return 'neutral';
            }
            case OfferStatus.CanBeSend:
            case OfferStatus.WaitingForConfirmation:
            case OfferStatus.Sent: { 
                return 'primary';
            }
            case OfferStatus.Accepted: { 
                return 'success';
            }
            case OfferStatus.WaitingForCancellation:
            case OfferStatus.Canceled:
            case OfferStatus.InvalidItems:
            case OfferStatus.RolledBack: { 
                return 'danger';
            }
            case OfferStatus.Hold:
            case OfferStatus.Undefined:
            default: { 
                return 'warning';
             }
         }
    }

    const getStatusDescription = (offer: OfferInfo):string => {
        return offer.status === OfferStatus.Sent
            ? `${OfferStatusDescription[offer.status]} ${formatDate(offer.sentAt)}`
            : OfferStatusDescription[offer.status];
    }

    const showHistory = (offerId: number) => {
        setSelectedOffer(offerId);
        setHistoryOpen(true);
    }

    return (
        <React.Fragment>
            {historyOpen &&
                <OfferHistory id = {selectedOffer}
                              open = {historyOpen}
                              setOpen = {setHistoryOpen}/>
            }
            {loading ? <CircularProgress/>
            :<>
                <Sheet
                    className="OrderTableContainer"
                    sx={{
                        display: {xs: 'none', sm: 'initial'},
                        width: '100%',
                        borderRadius: 'sm',
                        flexShrink: 1,
                        overflow: 'auto',
                        height: 'auto',
                        maxHeight: '50dvh'
                    }}
                >
                    <Table
                        aria-labelledby="tableTitle"
                        stickyHeader
                        hoverRow
                        sx={{
                            '--TableCell-headBackground': 'var(--joy-palette-background-level1)',
                            '--Table-headerUnderlineThickness': '1px',
                            '--TableRow-hoverBackground': 'var(--joy-palette-background-level1)',
                            '--TableCell-paddingY': '4px',
                            '--TableCell-paddingX': '8px',
                        }}
                    >
                        <thead>
                            <tr>
                                <th style={{width: 50, padding: '12px 6px'}}>#</th>
                                <th style={{width: 90, padding: '12px 6px'}}>Тип</th>
                                <th style={{width: 90, padding: '12px 6px'}}>Создано/Отправлено</th>
                                <th style={{width: 140, padding: '12px 6px'}}>Статус</th>
                                <th style={{width: 90, padding: '12px 6px'}}>Партнер</th>
                                <th style={{width: 50, padding: '12px 6px'}}>Бот</th>
                                <th style={{width: 200, padding: '12px 6px'}}>Доп.Информация</th>
                                <th style={{width: 50, padding: '12px 6px'}}></th>
                            </tr>
                        </thead>
                        <tbody>
                            {offers.map((row) => (
                                <tr key={row.id}>
                                    <td>
                                        <Stack spacing={1}
                                               direction={'column'}>
                                            <Stack spacing={1}
                                                direction={'row'}
                                                sx={{
                                                        justifyContent: "left",
                                                        alignItems: "center",
                                                }}>
                                                <Typography level="body-xs">{row.id}</Typography>
                                                <IconButton
                                                    size="sm"
                                                    variant="outlined"
                                                    color="neutral"
                                                    onClick={() => showHistory(row.id)}
                                                >
                                                    <AutoStoriesIcon/>
                                                </IconButton>
                                            </Stack>
                                            {row.groupId && <Typography level="body-xs">({row.groupId})</Typography>}
                                        </Stack>
                                    </td>
                                    <td><Typography level="body-xs">{OfferTypeDescription[row.type]}</Typography></td>
                                    <td>
                                        <Stack spacing={1} direction={'column'}>
                                            <Typography level="body-xs">{formatDateTime(row.created)}</Typography>
                                            {row.sentAt ? <Typography level="body-xs">{formatDateTime(row.sentAt)}</Typography> : <></>}
                                        </Stack>
                                    </td>
                                    <td>
                                        <Stack spacing={1} direction={'column'}>
                                            <Chip
                                                variant="soft"
                                                size="sm"
                                                startDecorator={getStatusIcon(row.status)}
                                                color={getStatusColor(row.status)}
                                            >
                                                {getStatusDescription(row)}
                                            </Chip>
                                            {row.steamStatus &&
                                                <Typography level="body-xs">{`SteamOffer #${row.steamOfferId}: ${SteamApiTradeOfferState[row.steamStatus]}`}</Typography>}
                                            {row.steamTradeStatus &&
                                                <Typography level="body-xs">{`SteamTrade #${row.steamTradeId}: ${SteamApiTradeStatus[row.steamTradeStatus]}`}</Typography>}
                                        </Stack>
                                    </td>
                                    <td>
                                        <Stack spacing={1} direction={'column'}>
                                            <Link
                                                underline="hover"
                                                href={`/app/users/${row.userId.toString()}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                level="body-xs"
                                            >
                                                {row.userId.toString()}
                                            </Link>
                                            <Link
                                                underline="hover"
                                                href={`https://steamcommunity.com/profiles/${row.userId.toString()}/`}
                                                target="_blank"
                                                level="body-xs"
                                            >
                                                (Steam link)
                                            </Link>
                                        </Stack>
                                    </td>
                                    <td>
                                        {row.botId
                                        ?<Link
                                            underline="hover"
                                            href={`/app/bots/${row.botId}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            level="body-xs"
                                        >
                                            #{row.botId}
                                        </Link>
                                        :<></>}
                                    </td>
                                    <td>
                                        <Stack spacing={1} direction={'column'}>
                                            <Typography level="body-xs">
                                                Обработка: {
                                                row.handleMode === OfferHandleMode.ByTime
                                                    ? row.handleDelaySeconds
                                                        ? `Через ${row.handleDelaySeconds}сек. после создания`
                                                        : OfferHandleModeDescription[OfferHandleMode.Manually]
                                                    : OfferHandleModeDescription[row.handleMode ?? OfferHandleMode.Instant]
                                                }
                                            </Typography>
                                            {row.cancellationReason !== undefined
                                            ?<Typography level="body-xs" textColor={'danger.outlinedColor'}>
                                                {OfferCancellationReasonDescription[row.cancellationReason]}
                                            </Typography>
                                            :<></>}
                                            {row.lastError
                                            ?<Typography level="body-xs" textColor={'danger.darkChannel'}>{row.lastError}</Typography>
                                            :<></>}
                                        </Stack>
                                    </td>
                                    <td style={{
                                        alignContent: 'center',
                                        textAlign: 'center'
                                    }}>
                                        {row.turnOnManualMode
                                        ?<Tooltip title='Принято решение о переводе заказа в ручной режим'>
                                            <Chip
                                                variant="soft"
                                                size="sm"
                                                startDecorator={<BuildIcon/>}
                                                color='warning'
                                            />
                                        </Tooltip>
                                        :<></>}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </Sheet>
                <Box sx={{display: {xs: 'block', sm: 'none'}}}>
                    {offers.map((listItem) => (
                        <List key={listItem.id} size="sm" sx={{'--ListItem-paddingX': 0}}>
                            <ListItem
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'start',
                                }}
                            >
                                <ListItemContent sx={{display: 'flex', gap: 2, alignItems: 'start'}}>
                                    <div>
                                        <Typography gutterBottom sx={{fontWeight: 600}}>#{listItem.id}</Typography>
                                        <Typography gutterBottom>[{OfferTypeDescription[listItem.type]}]</Typography>
                                        <Typography gutterBottom>Создано: {formatDateTime(listItem.created)}</Typography>
                                        <Stack spacing={1}
                                               direction={'row'}
                                               sx={{
                                                    justifyContent: "left",
                                                    alignItems: "center"
                                                }}
                                        >
                                            <Typography>Партнер:</Typography>
                                            <Link
                                                underline="hover"
                                                href={`/app/users/${listItem.userId.toString()}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                level="body-xs"
                                                fontSize={14}
                                            >
                                                {listItem.userId.toString()}
                                            </Link>
                                            <Link
                                                underline="hover"
                                                href={`https://steamcommunity.com/profiles/${listItem.userId.toString()}/`}
                                                target="_blank"
                                                level="body-xs"
                                                style={{
                                                    minWidth: 80,
                                                    fontSize: 14
                                                }}
                                            >
                                                (Steam link)
                                            </Link>
                                        </Stack>
                                        {listItem.botId
                                        ?<Stack spacing={1}
                                                direction={'row'}
                                                sx={{
                                                    justifyContent: "left",
                                                    alignItems: "center"
                                                }}>
                                            <Typography>Бот:</Typography>
                                            <Link
                                                underline="hover"
                                                href={`/app/bots/${listItem.botId}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                level="body-xs"
                                                fontSize={14}
                                            >
                                                #{listItem.botId}
                                            </Link>
                                        </Stack>
                                        :<></>}
                                        {listItem.steamStatus &&
                                            <Typography level="body-xs">{`SteamOffer #${listItem.steamOfferId}: ${SteamApiTradeOfferState[listItem.steamStatus]}`}</Typography>}
                                        {listItem.steamTradeStatus &&
                                            <Typography level="body-xs">{`SteamTrade #${listItem.steamTradeId}: ${SteamApiTradeStatus[listItem.steamTradeStatus]}`}</Typography>}
                                        <Typography level="body-xs">
                                            Обработка: {
                                            listItem.handleMode === OfferHandleMode.ByTime
                                                ? listItem.handleDelaySeconds
                                                    ? `Через ${listItem.handleDelaySeconds}сек. после создания`
                                                    : OfferHandleModeDescription[OfferHandleMode.Manually]
                                                : OfferHandleModeDescription[listItem.handleMode ?? OfferHandleMode.Instant]
                                            }
                                        </Typography>
                                        {listItem.cancellationReason !== undefined
                                        ?<Typography level="body-xs" textColor={'danger.outlinedColor'}>
                                            {OfferCancellationReasonDescription[listItem.cancellationReason]}
                                        </Typography>
                                        :<></>}
                                        {listItem.lastError
                                        ?<Typography level="body-xs" textColor={'danger.darkChannel'}>Доп.Информация: {listItem.lastError}</Typography>
                                        :<></>}
                                    </div>
                                </ListItemContent>
                                <Chip
                                    variant="soft"
                                    size="sm"
                                    startDecorator={getStatusIcon(listItem.status)}
                                    color={getStatusColor(listItem.status)}
                                >
                                    {getStatusDescription(listItem)}
                                </Chip>
                                {listItem.turnOnManualMode
                                ?<Tooltip title='Принято решение о переводе заказа в ручной режим'>
                                    <Chip
                                        variant="soft"
                                        size="sm"
                                        startDecorator={<BuildIcon/>}
                                        color='warning'
                                    />
                                </Tooltip>
                                :<></>}
                                <IconButton
                                    size="sm"
                                    variant="outlined"
                                    color="neutral"
                                    onClick={() => showHistory(listItem.id)}
                                >
                                    <AutoStoriesIcon/>
                                </IconButton>
                            </ListItem>
                            <ListDivider/>
                        </List>
                    ))}
                </Box>
            </>}
        </React.Fragment>
    );
}
